import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Button from './Button';
import { 
  TalentType, 
  PlayerTalent,
  getTalentDisplayName,
  getTalentIcon,
  calculateTalentEffect
} from '../types/talents';
import { useAppTranslation } from '../i18n/useAppTranslation';

interface TalentCardProps {
  talent: PlayerTalent;
  onUpgradeClick?: (talentId: TalentType) => void;
  onViewClick?: (talentId: TalentType) => void;
  canUpgrade?: boolean;
  isUpgrading?: boolean;
  compact?: boolean;
  showUpgradeButton?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const TalentCard: React.FC<TalentCardProps> = ({
  talent,
  onUpgradeClick,
  onViewClick,
  canUpgrade = false,
  isUpgrading = false,
  compact = false,
  showUpgradeButton = true,
  className = '',
  style = {}
}) => {
  const { t } = useAppTranslation(["research", "common"]);
  const displayName = getTalentDisplayName(talent.talentId);
  const icon = getTalentIcon(talent.talentId);
  const currentLevel = talent.level;
  const maxLevel = talent.maxLevel;
  const isMaxLevel = currentLevel >= maxLevel;
  const progressPercent = (currentLevel / maxLevel) * 100;
  const currentEffect = calculateTalentEffect(talent.talentId, currentLevel);

  const cardHeight = compact ? 100 : 140;
  
  const tooltipContent = (
    <div style={{ 
      maxWidth: 280, 
      whiteSpace: 'normal',
      backgroundColor: '#1a1a1a',
      color: '#ffffff',
      padding: '12px',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.1)'
    }}>
      <div style={{ 
        background: isMaxLevel 
          ? 'linear-gradient(90deg, #FFD700 0%, #FF8C00 100%)'
          : 'linear-gradient(90deg, #00E0FF 0%, #5B86E5 100%)',
        color: isMaxLevel ? '#000' : '#fff',
        padding: '6px 10px',
        borderRadius: 4,
        fontWeight: 700,
        marginBottom: 6,
        textAlign: 'center'
      }}>
        {displayName}{" "}
        {isMaxLevel && `(${t("research:talentsUi.maxShort")})`}
      </div>
      
      <div>
        <strong>{t("research:talentsUi.description")}:</strong>{" "}
        {talent.description}
      </div>
      <div style={{ marginTop: 6 }}>
        <strong>{t("research:talentsUi.currentEffect")}:</strong> {currentEffect}
      </div>
      
      <div style={{ marginTop: 6 }}>
        <strong>{t("common:labels.level")}:</strong> {currentLevel}/{maxLevel}
      </div>
    </div>
  );

  return (
    <OverlayTrigger overlay={<Tooltip>{tooltipContent}</Tooltip>}>
      <div 
        className={`talent-card p-3 border rounded cursor-pointer ${
          isMaxLevel ? 'border-warning' : 'border-info'
        } ${className}`}
        style={{
          minHeight: cardHeight,
          transition: 'all 0.3s ease',
          position: 'relative',
          backgroundColor: isMaxLevel 
            ? 'rgba(255, 193, 7, 0.1)' 
            : 'rgba(13, 202, 240, 0.1)',
          backgroundImage: isMaxLevel 
            ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.05) 0%, rgba(255, 193, 7, 0.15) 100%)'
            : 'linear-gradient(135deg, rgba(13, 202, 240, 0.05) 0%, rgba(13, 202, 240, 0.15) 100%)',
          color: '#ffffff',
          ...style
        }}
        onClick={() => onViewClick?.(talent.talentId)}
      >
        <div className={`d-flex ${compact ? 'align-items-center' : 'align-items-start'} gap-${compact ? 2 : 3}`}>
          <img
            src={icon}
            alt={displayName}
            style={{
              width: compact ? 32 : 48,
              height: compact ? 32 : 48,
              borderRadius: compact ? '6px' : '8px',
              border: '2px solid rgba(255,255,255,0.2)',
              flexShrink: 0
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/assets/images/talents/default.png';
            }}
          />
          
          <div className="flex-grow-1 min-w-0">
            <h6 className={`mb-1 text-truncate ${compact ? 'fs-small' : ''}`}>
              {displayName}
            </h6>
            
            {!compact && (
              <div className="small text-muted mb-2 text-truncate">
                {talent.description}
              </div>
            )}
            
            {/* Level Display */}
            <div className="mb-2">
              <div className="small">
                <span>
                  {t("common:labels.level")} {currentLevel}/{maxLevel}
                </span>
              </div>
            </div>

            {/* Effect Display */}
            {!compact && (
              <div className="small text-success mb-2">
                <strong>{t("research:talentsUi.effect")}:</strong> {currentEffect}
              </div>
            )}

            {/* Status Badge for Compact Mode */}
            {compact && isMaxLevel && (
              <span className="badge bg-warning text-dark">
                {t("research:talentsUi.maxShort")}
              </span>
            )}
          </div>
        </div>

        {/* Action Button */}
        {showUpgradeButton && !isMaxLevel && canUpgrade && (
          <Button
            size="small"
            variant="wide-primary"
            loading={isUpgrading}
            disabled={isUpgrading}
            onClick={(e) => {
              e.stopPropagation();
              onUpgradeClick?.(talent.talentId);
            }}
            style={{ 
              position: 'absolute', 
              bottom: 8, 
              right: 8,
              fontSize: compact ? '0.7rem' : '0.8rem'
            }}
          >
            {t("common:actions.upgrade")}
          </Button>
        )}

        {isMaxLevel && !compact && (
          <div 
            className="small text-warning fw-bold"
            style={{ position: 'absolute', bottom: 8, right: 8 }}
          >
            {t("research:talentsUi.maxLevel")}
          </div>
        )}
      </div>
    </OverlayTrigger>
  );
};

export default TalentCard;
